import { Component, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { DocumentiService } from 'src/app/core/documenti.service';
import { DocumentiSearchDocRow, DocumentiSearchFilters, DocumentiSearchRequest } from 'src/app/shared/models/documento-search.model';
import { LoaderExclusionType, LoaderService } from '../../layout/loader/loader.service';
import { debounceTime, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { CommonModule, NgClass } from '@angular/common';
import { PromemoriaModule } from 'src/app/features/promemoria/promemoria.module';


interface Suggerimento {
  documentoId: number,
  dataDocumento?: Date,
  protocollo: string,
  protocolloMittente?: string | null,
  arrivo: boolean,
  oggetto?: string
}

@Component({
  selector: 'app-topbar-search',
  standalone: true,
  imports: [NgClass, RouterModule, AutoCompleteModule, FormsModule, CommonModule, PromemoriaModule],
  template: `
<div style="display: inline-flex; align-items: center;">
      <p-autoComplete
        #autoCompleteRef
        [style]="{'width':'70vh'}"
        [inputStyle]="{'width':'100%'}"
        scrollHeight="300px"
        [(ngModel)]="protocolloSelected"
        optionLabel="protocollo"
        showClear=true
        [suggestions]="suggerimenti()"
        (onFocus)="mostraSuggerimenti()"
        (onClear)="resetAutocomplete()"
        (onSelect)="navigateToProtocolPage($event)"
        (completeMethod)="searchProtocollo($event)"
        placeholder="Cerca protocollo">

        <ng-template pTemplate='header'>
          <div class="bg-primary text-white flex justify-content-center border-round-sm font-semibold p-1">{{header}}</div>
        </ng-template>

        <ng-template let-i pTemplate="item">
          <div class="p-0 flex flex-column">
            <p class="m-0 flex justify-content-start text-xs">{{this.formatDate(i.dataDocumento) }}</p>

            <div class="flex w-full align-content-center justify-content-between">
              <h5 class="m-0 pt-1 text-base">{{i.protocollo}}</h5>
              @if (i.protocolloMittente) {
                <p class="mt-1 mb-0 flex justify-content-start text-xs">Mitt.: <span class="font-bold">{{i.protocolloMittente}}</span></p>
              }
              <div [ngClass]="i.arrivo ? 'docIn' : 'docOut'" [title]="i.arrivo ? 'Documento in arrivo' : 'Documento in partenza'" class="docIn"></div>
            </div>
            <p class="m-0 flex justify-content-start text-xs">{{i.oggetto}}</p>

          </div>
        </ng-template>

        <ng-template  pTemplate="empty">
          <p class="mr-4">Nessun documento trovato</p>
        </ng-template>

      </p-autoComplete>
    </div>
  `,
  styles: `
      .docIn {
        display: inline-block;
        background-image: url("/assets/images/inbox-in-duotone.svg");
        background-repeat: no-repeat;
        width: 20px;
        height: 20px;
      }

      .docOut {
        display: inline-block;
        background-image: url("/assets/images/inbox-out-duotone.svg");
        background-repeat: no-repeat;
        width: 20px;
        height: 20px;
      }

      .min-h-80px {
        min-height:80px
      }
  `
})

export class TopbarSearchComponent implements OnInit, OnDestroy {


  private readonly documentiService = inject(DocumentiService)
  private readonly router = inject(Router);

  autoComplete = viewChild.required<AutoComplete>('autoCompleteRef');

  private readonly searchSubject$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  protocolloSelected: Suggerimento | undefined;
  suggerimenti = signal<Suggerimento[]>([]);
  header = '';


  ngOnInit(): void {

    this.searchSubject$.pipe(
      debounceTime(300),
      takeUntil(this.destroy$),
      switchMap((value: string) => {

        LoaderService.addExclusion('search', LoaderExclusionType.isContained);

        const request = {
          pageFirstRow: 0,
          pageSize: 10,
          filters: {
            protocolloOrProtocolloMittente: value
          } as DocumentiSearchFilters
        } as DocumentiSearchRequest;

        return this.documentiService.search(request, true)
          .pipe(tap(() => {
            LoaderService.removeExclusion('search');
            this.header = 'Documenti trovati';
          }));
      }))
      .subscribe(response => {
        const protocolli: Suggerimento[] = (response.results.map(item => item.data) as DocumentiSearchDocRow[]).map(prot => ({
          documentoId: prot.documentoId,
          dataDocumento: prot.dataDocumento,
          protocollo: prot.protocollo,
          protocolloMittente: prot.protocolloMittente,
          arrivo: prot.arrivo,
          oggetto: prot.oggetto
        }));
        this.suggerimenti.set(protocolli);
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  mostraSuggerimenti(): void {
    this.header = 'Documenti aperti di recente';

    LoaderService.addExclusion('recenti', LoaderExclusionType.isContained);
    this.documentiService.getDocumentiRecentiByUtente()
    .pipe( tap( () => { LoaderService.removeExclusion('recenti'); }))
    .subscribe(d => {
      this.suggerimenti.set(d.map( item => {
        return ({
          documentoId: item.documento.documentoId,
          dataDocumento: item.documento.dataDocumento,
          protocollo: item.documento.protocollo,
          protocolloMittente: item.documento.protocolloMittente,
          arrivo: item.documento.arrivo,
          oggetto: item.documento.oggetto
        })
      }));
    });

    if (this.autoComplete && !this.autoComplete().value) {
      setTimeout(() => {
        this.autoComplete().show();
      }, 0);
    }
  }

  public searchProtocollo(event: AutoCompleteCompleteEvent): void {
    const query = event.query;
    if (!query || query.length === 0) {
      this.resetAutocomplete();
      return;
    }
    if (query) {
      this.searchSubject$.next(query);
    }
  }

  public navigateToProtocolPage(event: AutoCompleteSelectEvent): void {
    this.router.navigate(['/visualizza', event.value.documentoId]);
  }

  public resetAutocomplete(): void {
    this.header = '';
    this.protocolloSelected = undefined;
    this.suggerimenti.set([]);
  }

  public formatDate(value: string): string {
    const data = new Date(value);
    // Verifica se la data è valida
    if (Number.isNaN(data.getTime())) {
      return value;
    }
    return data.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }
}

